// Poll Admin Panel
// Admin interface for creating and managing polls

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Clock, 
  Users, 
  BarChart3,
  Play,
  Pause,
  Settings,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Calendar,
  Timer,
  Target
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  createPoll, 
  updatePoll, 
  deletePoll, 
  getAllPolls, 
  listenToAllPolls,
  closePoll,
  extendPollDuration,
  getPollResults
} from '../../firebase/polls';
import toast from 'react-hot-toast';

const PollAdminPanel = () => {
  const { currentUser, logAdminAction } = useRealtime();
  const [polls, setPolls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('polls');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [pollResults, setPollResults] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    options: [{ id: 1, text: '' }, { id: 2, text: '' }],
    duration: 24, // hours
    category: 'hobbies',
    isActive: true
  });

  // Load polls
  useEffect(() => {
    const unsubscribe = listenToAllPolls((result) => {
      if (result.success) {
        setPolls(result.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle option changes
  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index].text = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  // Add new option
  const addOption = () => {
    const newId = Math.max(...formData.options.map(opt => opt.id)) + 1;
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { id: newId, text: '' }]
    }));
  };

  // Remove option
  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    }
  };

  // Create poll
  const handleCreatePoll = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.options.filter(opt => opt.text.trim()).length < 2) {
      toast.error('Please add at least 2 options');
      return;
    }

    try {
      const endTime = new Date();
      endTime.setHours(endTime.getHours() + formData.duration);

      const pollData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        options: formData.options.filter(opt => opt.text.trim()).map(opt => ({
          id: opt.id,
          text: opt.text.trim()
        })),
        endTime: endTime.toISOString(),
        category: formData.category,
        isActive: formData.isActive,
        createdBy: currentUser.uid
      };

      const result = await createPoll(pollData);
      
      if (result.success) {
        toast.success('Poll created successfully!');
        setShowCreateForm(false);
        resetForm();
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('create_poll', {
            pollId: result.pollId,
            title: pollData.title
          });
        }
      } else {
        toast.error('Failed to create poll: ' + result.error);
      }
    } catch (error) {
      toast.error('Error creating poll: ' + error.message);
    }
  };

  // Update poll
  const handleUpdatePoll = async () => {
    if (!editingPoll) return;

    try {
      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        options: formData.options.filter(opt => opt.text.trim()).map(opt => ({
          id: opt.id,
          text: opt.text.trim()
        })),
        category: formData.category,
        isActive: formData.isActive
      };

      const result = await updatePoll(editingPoll.id, updateData);
      
      if (result.success) {
        toast.success('Poll updated successfully!');
        setEditingPoll(null);
        resetForm();
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('update_poll', {
            pollId: editingPoll.id,
            title: updateData.title
          });
        }
      } else {
        toast.error('Failed to update poll: ' + result.error);
      }
    } catch (error) {
      toast.error('Error updating poll: ' + error.message);
    }
  };

  // Delete poll
  const handleDeletePoll = async (pollId, title) => {
    if (window.confirm(`Are you sure you want to delete the poll "${title}"? This action cannot be undone.`)) {
      try {
        const result = await deletePoll(pollId);
        
        if (result.success) {
          toast.success('Poll deleted successfully!');
          
          // Log admin action
          if (logAdminAction) {
            await logAdminAction('delete_poll', {
              pollId,
              title
            });
          }
        } else {
          toast.error('Failed to delete poll: ' + result.error);
        }
      } catch (error) {
        toast.error('Error deleting poll: ' + error.message);
      }
    }
  };

  // Close poll
  const handleClosePoll = async (pollId, title) => {
    try {
      const result = await closePoll(pollId);
      
      if (result.success) {
        toast.success('Poll closed successfully!');
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('close_poll', {
            pollId,
            title
          });
        }
      } else {
        toast.error('Failed to close poll: ' + result.error);
      }
    } catch (error) {
      toast.error('Error closing poll: ' + error.message);
    }
  };

  // View poll results
  const handleViewResults = async (poll) => {
    try {
      const result = await getPollResults(poll.id);
      
      if (result.success) {
        setPollResults(result.data);
        setSelectedPoll(poll);
        setActiveTab('results');
      } else {
        toast.error('Failed to load poll results: ' + result.error);
      }
    } catch (error) {
      toast.error('Error loading poll results: ' + error.message);
    }
  };

  // Edit poll
  const handleEditPoll = (poll) => {
    setEditingPoll(poll);
    setFormData({
      title: poll.title,
      description: poll.description,
      options: poll.options.map(opt => ({ id: opt.id, text: opt.text })),
      duration: 24,
      category: poll.category || 'hobbies',
      isActive: poll.isActive
    });
    setShowCreateForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      options: [{ id: 1, text: '' }, { id: 2, text: '' }],
      duration: 24,
      category: 'hobbies',
      isActive: true
    });
    setEditingPoll(null);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Check if poll is expired
  const isPollExpired = (poll) => {
    if (!poll.endTime) return false;
    return new Date(poll.endTime) <= new Date();
  };

  // Get poll status
  const getPollStatus = (poll) => {
    if (!poll.isActive) return { text: 'Closed', color: 'red' };
    if (isPollExpired(poll)) return { text: 'Expired', color: 'orange' };
    return { text: 'Active', color: 'green' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading polls...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Target className="mr-3 text-purple-600" size={32} />
                Poll Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Create and manage interactive polls for hobbies section
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Poll
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'polls', label: 'All Polls', icon: BarChart3 },
                { id: 'results', label: 'Results', icon: TrendingUp }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'polls' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    All Polls ({polls.length})
                  </h3>
                </div>

                {polls.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No polls found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Create your first poll to get started
                    </p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Create Poll
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {polls.map((poll) => {
                      const status = getPollStatus(poll);
                      return (
                        <motion.div
                          key={poll.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {poll.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {poll.description}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-1" />
                                  <span>{poll.totalVotes || 0} votes</span>
                                </div>
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  <span>{formatDate(poll.createdAt?.toDate?.() || poll.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                            
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              status.color === 'green' 
                                ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300'
                                : status.color === 'orange'
                                ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300'
                                : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300'
                            }`}>
                              {status.text}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4">
                            {poll.options.slice(0, 3).map((option, index) => (
                              <div key={option.id} className="text-sm text-gray-600 dark:text-gray-400">
                                {index + 1}. {option.text}
                              </div>
                            ))}
                            {poll.options.length > 3 && (
                              <div className="text-sm text-gray-500 dark:text-gray-500">
                                +{poll.options.length - 3} more options
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewResults(poll)}
                              className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Results
                            </button>
                            
                            <button
                              onClick={() => handleEditPoll(poll)}
                              className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                            
                            {poll.isActive && !isPollExpired(poll) && (
                              <button
                                onClick={() => handleClosePoll(poll.id, poll.title)}
                                className="flex items-center px-3 py-1 text-sm bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200"
                              >
                                <Pause className="h-4 w-4 mr-1" />
                                Close
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleDeletePoll(poll.id, poll.title)}
                              className="flex items-center px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'results' && selectedPoll && pollResults && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Poll Results: {selectedPoll.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {pollResults.totalVotes} total votes
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {Object.values(pollResults.results).map((result, index) => (
                    <div key={result.optionId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {result.text}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {result.votes} votes
                          </span>
                          <span className="text-sm font-bold text-purple-600">
                            {result.percentage}%
                          </span>
                        </div>
                      </div>
                      
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-purple-500 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${result.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Poll Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {editingPoll ? 'Edit Poll' : 'Create New Poll'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Poll Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter poll title..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter poll description..."
                    />
                  </div>

                  {/* Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Poll Options *
                    </label>
                    <div className="space-y-3">
                      {formData.options.map((option, index) => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            placeholder={`Option ${index + 1}...`}
                          />
                          {formData.options.length > 2 && (
                            <button
                              onClick={() => removeOption(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addOption}
                      className="mt-2 flex items-center text-sm text-purple-600 hover:text-purple-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Option
                    </button>
                  </div>

                  {/* Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Duration (hours)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        value={formData.duration}
                        onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="hobbies">Hobbies</option>
                        <option value="entertainment">Entertainment</option>
                        <option value="sports">Sports</option>
                        <option value="general">General</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Poll is active
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingPoll ? handleUpdatePoll : handleCreatePoll}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingPoll ? 'Update Poll' : 'Create Poll'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PollAdminPanel;
